import { ReactElement, SyntheticEvent, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cc from 'classcat';
import { sortMarketsByMainOrder } from 'utils/collection-manipulation/array-sort-by';
import { EIconSize } from 'utils/constants/app/ui';
import { GAME_TYPE_LIVE } from 'utils/constants/sportsbook/generic';
import {
  ADVANCED_CONTAINER_SIZE,
  FAVORITE_BUTTON_CONTAINER_ICON_SIZE
} from 'utils/constants/sportsbook/magic-numbers';
import { MATCH_TRACKER_AVAILABLE_SPORTS } from 'utils/constants/sportsbook/match-tracker-sports';
import { NOTIFICATION_SUPPORTING_SPORTS } from 'utils/constants/sportsbook/notificationSupportSports';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { ADD_GAME_CARDS } from 'utils/constants/tour';
import { GLOBALS } from 'utils/generic/global-variables';
import {
  mountPoint,
  sbRedirect,
  shouldRedirect
} from 'utils/generic/route-manipulation';
import { checkIsNotESportType } from 'utils/sportsbook/check-type-is-not-sportbook';
import { ENotRealSports } from 'interfaces/not-real-sports';
import {
  ETeamNumber,
  TAdvancedSelectedMarkets,
  TGameMainInfoType,
  TGameMarketsInfoType,
  TSelectedMarketTypeNames
} from 'interfaces/sportsbook';
import { ESportsbookLayoutTypes } from 'interfaces/sportsbook-configs';
import { Game, Market } from 'interfaces/sportsbook-data-levels';
import { AFTER_SIGN_IN_ACTION_TYPES } from 'services/afterSignIn';
import { EventsRow } from './EventsRow';
import { ExpandedMarkets } from './ExpandedMarkets';
import { EventAction } from 'components/EventAction';
import { FavoriteButtonGame } from 'components/FavoriteButton';
import { GlobalIcon } from 'components/GlobalIcon';
import { saveCurrentLocation } from 'components/QueryRoute';
import { TeamLogo } from 'components/TeamLogo';
import { GameTime } from 'newcomponents/Shared/GameTime';
import { MatchStatistic } from 'newcomponents/Shared/sportsbook/MatchStatistic';
import { NotificationIcon } from 'newcomponents/Shared/sportsbook/NotificationIcon';
import { VideoCamIcon } from 'newcomponents/Shared/sportsbook/VideoCamIcon';
import { useSportsbookConfigs } from 'hooks/sportsbook/useSportsbookConfigs';
import { useCustomHistory } from 'newhooks/generic/useCustomHistory';
import { useSbRouteMatch } from 'newhooks/generic/useSbRouteMatch';
import { useOpenBetBuilder } from 'newhooks/sportsbook/useOpenBetBuilder';
import { resetMarketTypes } from 'store/actions';
import { getOddFormat } from 'store/selectors/betslip';
import './index.less';

const MAPPING: Record<number, keyof TSelectedMarketTypeNames> = {
  0: 'first',
  1: 'second',
  2: 'third'
};

type TAdvancedMatchCardProps = {
  gameGeneralInfo: TGameMainInfoType;
  selectedMarkets: TAdvancedSelectedMarkets;
  selectedEventIds: string[];
  isLiveStreamingAvailable?: boolean;
  gameMarketsInfo?: TGameMarketsInfoType;
  showSportName?: boolean;
  selectedMarketTypeNames?: TSelectedMarketTypeNames;
};

export const AdvancedMatchCard = ({
  isLiveStreamingAvailable = true,
  gameGeneralInfo,
  gameMarketsInfo,
  selectedMarkets,
  selectedEventIds,
  showSportName,
  selectedMarketTypeNames
}: TAdvancedMatchCardProps): ReactElement => {
  const { push } = useCustomHistory();
  const configs = useSportsbookConfigs();
  const dispatch = useDispatch();
  const { isExact } = useSbRouteMatch(
    `${mountPoint(location.pathname)}/team/:sport/:teamId?`
  );

  const oddFormat = useSelector(getOddFormat);

  const [expandedMarkets, setExpandedMarkets] = useState(false);

  const isLive = gameGeneralInfo.type === GAME_TYPE_LIVE;
  const sportAlias = gameGeneralInfo.sport_alias || '';

  const matchTrackerAvailable =
    Boolean(gameGeneralInfo.video_id) ||
    Boolean(gameGeneralInfo.video_id2) ||
    (gameGeneralInfo.sport_alias &&
      MATCH_TRACKER_AVAILABLE_SPORTS.includes(sportAlias));

  const availableIcons = {
    videoCam: !!isLiveStreamingAvailable,
    matchTracker: matchTrackerAvailable,
    notification:
      SpringConfigs.PUSH_NOTIFICATIONS_ENABLED &&
      NOTIFICATION_SUPPORTING_SPORTS.includes(sportAlias),
    statistics:
      SpringConfigs.STATISTICS_SWITCHER && gameGeneralInfo.is_stat_available
  };

  const selectedMarketsToShow: Record<string, Market> = useMemo(() => {
    return Object.entries(selectedMarkets).reduce(
      (acc: Record<string, Market>, [key, value]) => {
        acc[key] = gameMarketsInfo
          ? sortMarketsByMainOrder(Object.values(gameMarketsInfo)).find(
              market => market?.type === value
            ) || ({} as Market)
          : ({} as Market);

        return acc;
      },
      {}
    );
  }, [gameMarketsInfo, selectedMarkets]);

  const handleMatchCardClick = () => {
    if (isExact) {
      GLOBALS.sportsBook.redirectedFromFavorite = true;
    }

    if (configs.spbLayoutType === ESportsbookLayoutTypes.ADVANCED) {
      const redirectPath = `${mountPoint(window.getPathname())}/${sportAlias}/${
        gameGeneralInfo.regionAlias
      }/${gameGeneralInfo.competitionId}/${gameGeneralInfo.id}?${
        isLive ? `gameType=${GAME_TYPE_LIVE}` : ''
      }`;

      push(redirectPath);

      return;
    }

    if (
      shouldRedirect(
        isLive ? SpringConfigs.PAGE_URLS.live : SpringConfigs.PAGE_URLS.prematch
      )
    ) {
      const redirectPath = `${
        isLive ? SpringConfigs.PAGE_URLS.live : SpringConfigs.PAGE_URLS.prematch
      }/${SpringConfigs.SPORTSBOOK_MOUNT_PATH}/${sportAlias}/${
        gameGeneralInfo.regionAlias
      }/${gameGeneralInfo.competitionId}/${gameGeneralInfo.id}`.replace(
        /^\//,
        ''
      );

      sbRedirect(redirectPath, false, true);
    } else {
      if (window.getPathname().includes(`/${ENotRealSports.team}`)) {
        saveCurrentLocation('mobileSportsbookGames');
      }

      const redirectPath = `${mountPoint(window.getPathname())}/${sportAlias}/${
        gameGeneralInfo.regionAlias
      }/${gameGeneralInfo.competitionId}/${gameGeneralInfo.id}`;

      push(redirectPath);
      dispatch(resetMarketTypes());
    }
  };

  const sportId = gameGeneralInfo.sportId;

  const betBuilderProps = {
    sportId,
    fixtureId: gameGeneralInfo.sportcast_id,
    gameTitle: `${gameGeneralInfo.team1_name} - ${gameGeneralInfo.team2_name}`,
    afterSignInActonName: `MatchCardInline_${gameGeneralInfo.id}_${AFTER_SIGN_IN_ACTION_TYPES.TOGGLE_BET_BUILDER_MODAL}`
  };

  const { openBetBuilder } = useOpenBetBuilder(betBuilderProps);

  const showBetBuilder =
    !isLive &&
    gameGeneralInfo.sportcast_id &&
    SpringConfigs.BET_BUILDER_SWITCHER;

  const isOutright =
    gameGeneralInfo?.show_type === ENotRealSports.outright.toUpperCase();

  return (
    <>
      <div
        className="advancedMatchCard"
        {...(SpringConfigs.USER_PERSONALIZATION_ENABLED
          ? { 'data-tour': ADD_GAME_CARDS }
          : {})}
      >
        <div className="advancedMatchCard__header">
          <FavoriteButtonGame
            className="advancedMatchCard__favoriteIcon"
            size={EIconSize._16}
            iconContainerSize={FAVORITE_BUTTON_CONTAINER_ICON_SIZE}
            game={{ id: gameGeneralInfo.id, type: gameGeneralInfo.type }}
          />
          <GameTime
            currentSport={sportAlias}
            game={gameGeneralInfo as Game}
            isLive={isLive}
            dir="row"
          />
          <span className="advancedMatchCard__header__regionInfo">
            <GlobalIcon
              lib="flags"
              size={EIconSize._20}
              name={gameGeneralInfo.regionAlias}
            />
            {showSportName ? `${gameGeneralInfo.sportName} ` : ''}
            {gameGeneralInfo.regionName} : {gameGeneralInfo.competitionName}
          </span>
        </div>
        <div className="advancedMatchCard__main">
          <div
            className={cc([
              'advancedMatchCard__main__matchInfo',
              {
                'advancedMatchCard__main__matchInfo--live': isLive
              }
            ])}
          >
            <div
              onClick={handleMatchCardClick}
              className={cc([
                'advancedMatchCard__teamsInfo globalEllipsis',
                {
                  'advancedMatchCard__teamsInfo--live': isLive
                }
              ])}
            >
              <div className="advancedMatchCard__teamsInfo__team">
                <div className="advancedMatchCard__teamsInfo__team__name globalEllipsis">
                  <TeamLogo
                    size="sm"
                    sizeByPixels={EIconSize._20}
                    teamId={gameGeneralInfo.team1_id}
                    isHomeOrAway={ETeamNumber.HOME}
                    isNotESport={checkIsNotESportType(gameGeneralInfo)}
                  >
                    <span className="iconWithTeamName">
                      {String(gameGeneralInfo.team1_name)
                        .substr(0, 2)
                        .toUpperCase()}
                    </span>
                  </TeamLogo>
                  <span
                    title={gameGeneralInfo.team1_name}
                    className="globalEllipsis"
                  >
                    {gameGeneralInfo.team1_name}
                  </span>
                </div>
                {isLive && (
                  <div className="advancedMatchCard__teamsInfo__team__score">
                    {gameGeneralInfo.info.score1}
                  </div>
                )}
              </div>
              {gameGeneralInfo.team2_name && (
                <div className="advancedMatchCard__teamsInfo__team">
                  <div className="advancedMatchCard__teamsInfo__team__name globalEllipsis">
                    <TeamLogo
                      size="sm"
                      sizeByPixels={EIconSize._20}
                      teamId={gameGeneralInfo.team2_id}
                      isHomeOrAway={ETeamNumber.AWAY}
                      isNotESport={checkIsNotESportType(gameGeneralInfo)}
                    >
                      <span className="iconWithTeamName">
                        {String(gameGeneralInfo.team2_name)
                          .substr(0, 2)
                          .toUpperCase()}
                      </span>
                    </TeamLogo>
                    <span
                      title={gameGeneralInfo.team2_name}
                      className="globalEllipsis"
                    >
                      {gameGeneralInfo.team2_name}
                    </span>
                  </div>
                  {isLive && (
                    <div className="advancedMatchCard__teamsInfo__team__score">
                      {gameGeneralInfo.info.score2}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div
              className={cc([
                'advancedMatchCard__iconsGroup',
                {
                  'advancedMatchCard__iconsGroup--withBorder': !isLive
                }
              ])}
            >
              <div className="advancedMatchCard__iconsGroup__col">
                {availableIcons.videoCam && (
                  <VideoCamIcon
                    size={EIconSize._16}
                    iconContainerSize={ADVANCED_CONTAINER_SIZE}
                  />
                )}
                {availableIcons.matchTracker && (
                  <EventAction
                    iconContainerSize={ADVANCED_CONTAINER_SIZE}
                    icon={
                      <GlobalIcon
                        lib="generic"
                        name="matchTrackerTab"
                        theme="default"
                        size={EIconSize._16}
                        className="matchTrackerIcon"
                      />
                    }
                  />
                )}
                {showBetBuilder &&
                  (SpringConfigs.HIDE_BET_BUILDER &&
                  gameGeneralInfo.regionAlias === 'Cyprus' ? null : (
                    <EventAction
                      iconContainerSize={ADVANCED_CONTAINER_SIZE}
                      icon={
                        <GlobalIcon
                          lib="generic"
                          name="bbIconOutline"
                          theme="default"
                          size={EIconSize._16}
                          className="black-45"
                          onClick={(e: SyntheticEvent) => openBetBuilder(e)}
                        />
                      }
                    />
                  ))}
              </div>
              <div className="advancedMatchCard__iconsGroup__col">
                {availableIcons.notification && (
                  <NotificationIcon
                    game={gameGeneralInfo}
                    currentSport={sportAlias}
                    size={EIconSize._16}
                    iconContainerSize={ADVANCED_CONTAINER_SIZE}
                  />
                )}

                {availableIcons.statistics && (
                  <MatchStatistic
                    matchId={gameGeneralInfo.id}
                    showStatisticInPopover={!isLive}
                    size={EIconSize._16}
                    iconContainerSize={ADVANCED_CONTAINER_SIZE}
                  />
                )}
              </div>
            </div>
          </div>
          <div className="advancedMatchCard__main__oddsWrapper">
            {!isOutright &&
              Object.entries(selectedMarketsToShow).map(
                ([rowKey, market], index) => (
                  <EventsRow
                    key={rowKey}
                    market={market}
                    selectedEventIds={selectedEventIds}
                    gameInfo={{
                      info: gameGeneralInfo.info,
                      gameId: gameGeneralInfo.id,
                      start_ts: gameGeneralInfo.start_ts,
                      team1_name: gameGeneralInfo.team1_name,
                      team2_name: gameGeneralInfo.team2_name,
                      sportAlias: gameGeneralInfo.sport_alias || '',
                      regionName: gameGeneralInfo.regionName || '',
                      sportName: gameGeneralInfo.sportName || '',
                      competitionName: gameGeneralInfo.competitionName,
                      sportId: gameGeneralInfo.sportId
                    }}
                    isLive={isLive}
                    isBlocked={gameGeneralInfo.is_blocked}
                    eventsCount={
                      selectedMarketTypeNames?.[MAPPING[index]]?.length
                    }
                    oddFormat={oddFormat}
                  />
                )
              )}
          </div>
          <div
            className="advancedMatchCard__main__expandIcon"
            onClick={() => setExpandedMarkets(prev => !prev)}
          >
            <GlobalIcon
              color="var(--v3-black-45)"
              lib="generic"
              name="caretDownSmall"
              theme="default"
              size={EIconSize._24}
              className={cc([
                {
                  'advancedMatchCard__main__expandIcon--expanded':
                    expandedMarkets
                }
              ])}
            />
          </div>
        </div>
      </div>
      {expandedMarkets && gameGeneralInfo && (
        <ExpandedMarkets
          game={gameGeneralInfo}
          isLive={isLive}
          handleShowMoreClick={handleMatchCardClick}
        />
      )}
    </>
  );
};
